import * as React from "react";
import { nx, raise, elev, cn, NxKeyline, AikoMark, type NxSize } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Verification harness (screenshot-verify pass for #138 / #158).
 * Exercises the Onyx Hex material straight from the typed barrel helpers:
 * the five notch silhouettes, the hexagon, the keycap, and the clip-safe
 * elevation + the CONTINUOUS keyline (the `NxKeyline` SVG stroke overlay).
 * Reports live `shape()` support so we know whether the notch renders or the
 * @supports fallback (rounded rect) is in effect.
 *
 * #158 acceptance gate (verify in a real browser): the keyline must trace the
 * FULL outline — every side, every rounded corner, AND the top-right notch
 * chamfer — with ZERO gaps, in both the resting and active states, at every
 * size. The refined flat-top hex should read tight (not wide) in its square
 * containers.
 */
function ShapeSupport() {
  const [supported, setSupported] = React.useState<boolean | null>(null);
  React.useEffect(() => {
    setSupported(
      typeof CSS !== "undefined" &&
        CSS.supports("clip-path", "shape(from 0 0, line to 0 0, close)"),
    );
  }, []);
  return (
    <span className="font-mono text-xs text-muted-foreground">
      CSS shape() supported: <b className="text-foreground">{String(supported)}</b>{" "}
      {supported === false ? "→ notch falls back to rounded-rect" : null}
    </span>
  );
}

const NOTCHES: { k: NxSize; label: string }[] = [
  { k: "l", label: "nx-l · panels" },
  { k: "s", label: "nx-s · cards" },
  { k: "m", label: "nx-m · tiles/fields" },
  { k: "b", label: "nx-b · buttons" },
  { k: "t", label: "nx-t · chips" },
];

export function OnyxHexMaterialDemo() {
  return (
    <Section
      id="onyx-hex-material"
      title="Onyx Hex material"
      description="Wave-0 shared chrome: the five top-right hex-notch silhouettes (.nx-*), the flat-top hexagon (.hex), the raised keycap (.raise, 3px bottom edge), and the clip-safe elevation + the CONTINUOUS keyline (NxKeyline SVG overlay — traces the full outline incl. the notch, where a border/inset-shadow gets clipped). Consumed by every Onyx Hex ticket via the typed barrel helpers."
    >
      <VariantBlock label="Notch silhouettes — continuous resting keyline (NxKeyline overlay, traces the notch)">
        <div className="flex flex-wrap items-end gap-6">
          {NOTCHES.map(({ k, label }) => (
            <div key={k} className="flex flex-col items-center gap-2">
              <div
                className={cn(
                  nx[k],
                  elev.card,
                  "relative flex h-24 w-32 items-center justify-center bg-card",
                )}
              >
                <span className="font-mono text-[11px] text-muted-foreground">{k.toUpperCase()}</span>
                <NxKeyline nx={k} />
              </div>
              <span className="font-mono text-[10px] text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      </VariantBlock>

      <VariantBlock label="Notch silhouettes — active keyline (accent ring + glow, continuous incl. notch)">
        <div className="flex flex-wrap items-end gap-6">
          {NOTCHES.map(({ k, label }) => (
            <div key={k} className="flex flex-col items-center gap-2">
              <div
                className={cn(
                  nx[k],
                  elev.card,
                  "nx-keyline-active relative flex h-24 w-32 items-center justify-center bg-card",
                )}
              >
                <span className="font-mono text-[11px] text-muted-foreground">{k.toUpperCase()}</span>
                <NxKeyline nx={k} active />
              </div>
              <span className="font-mono text-[10px] text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      </VariantBlock>

      <VariantBlock label="Notch on a panel — active selection (accent keyline + glow, clip-safe)">
        <div className={cn(nx.l, elev.panel, "nx-keyline-active relative w-80 bg-card p-5")}>
          <div className="text-sm font-semibold text-foreground">Focused panel</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Elevation via drop-shadow (box-shadow would be clipped); selection via the NxKeyline
            stroke overlay — continuous across the notch where an inset keyline drops out.
          </div>
          <NxKeyline nx="l" active />
        </div>
      </VariantBlock>

      <VariantBlock label="AikoMark + round status dots (replaces the hexagon)">
        <div className="flex items-center gap-5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-success" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-accent" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-destructive" />
          <AikoMark size="sm" aria-label="Aiko" />
          <AikoMark size="md" />
          <AikoMark size="xl" />
        </div>
      </VariantBlock>

      <VariantBlock label="Keycap (.raise) — flat face over a 3px solid bottom edge; presses on :active">
        <div className="flex items-center gap-4">
          <button className={cn(nx.b, raise, "bg-primary px-4 py-2 text-sm font-medium text-primary-foreground")}>
            Publish
          </button>
          <button className={cn(nx.b, raise, "relative bg-card px-4 py-2 text-sm font-medium text-foreground")}>
            Secondary
            <NxKeyline nx="b" />
          </button>
          <span className={cn("rounded-md bg-accent/10 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-accent")}>
            New
          </span>
        </div>
      </VariantBlock>

      <VariantBlock label="Environment">
        <ShapeSupport />
      </VariantBlock>
    </Section>
  );
}
